import React, {useEffect, useRef, useState} from 'react'
import {styled} from '@mui/system'

interface ModalTranslationProps {
  isLoad: boolean
}

const hideLoaderPages = ['/', '/maintenance', '/sandbox']

interface WebGLResources {
  gl: WebGLRenderingContext | null
  program: WebGLProgram | null
  vertexShader: WebGLShader | null
  fragmentShader: WebGLShader | null
  positionBuffer: WebGLBuffer | null
  positionLocation: number
  resolutionLocation: WebGLUniformLocation | null
  timeLocation: WebGLUniformLocation | null
  startTime: number
  setSize: (() => void) | null
}

const ModalLoader = ({isLoad}: ModalTranslationProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const webglResourcesRef = useRef<WebGLResources | null>(null)
  const isInitializedRef = useRef(false)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [pathname, setPathname] = useState(window.location.pathname)

  useEffect(() => {
    const updatePathname = () => {
      setPathname(window.location.pathname)
    }

    updatePathname()

    window.addEventListener('popstate', updatePathname)

    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args)
      updatePathname()
    }

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args)
      updatePathname()
    }

    return () => {
      window.removeEventListener('popstate', updatePathname)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [])

  useEffect(() => {
    if (isInitializedRef.current) return

    const canvas = canvasRef.current
    if (!canvas) return

    const gl = canvas.getContext('webgl', {alpha: true, antialias: true})
    if (!gl) return

    isInitializedRef.current = true

    const vertexShaderSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `

    const fragmentShaderSource = `
      precision mediump float;

      #define hexCount 3.
      #define rotSpeed 0.2
      #define moveSpeed vec2(0.5, 1.)
      #define palette 2

      #if(palette == 1)
        #define hexCol vec3(0.15, 0.45, 0.3)
        #define glowCol vec3(0., 0.15, 0.3)
      #elif(palette == 2)
        #define hexCol vec3(0.13, 0.36, 0.36)
        #define glowCol vec3(0.041, 0.206, 0.202)
      #elif(palette == 3)
        #define hexCol vec3(0.3, 0.3, 0.)
        #define glowCol vec3(0.15, 0.09, 0.)
      #elif(palette == 4)
        #define hexCol vec3(0.3, 0.3, 0.)
        #define glowCol vec3(0.3, 0.15, 0.)
      #elif(palette == 5)
        #define hexCol vec3(0.3, 0.3, 0.3)
        #define glowCol vec3(0., 0., 0.15)
      #else
        #define hexCol vec3(0.6)
        #define glowCol vec3(0.6)
      #endif

      uniform vec2 resolution;
      uniform float time;

      vec2 gridDist(vec2 pos){
        vec2 grid = vec2(2.*sqrt(3.), 2.);
        return abs(pos - floor(pos/grid + 0.5)*grid);
      }

      float hexDistFunc(vec2 pos){
        vec2 d1 = gridDist(pos);
        vec2 d2 = gridDist(pos - vec2(sqrt(3.), 1.));
        
        vec2 apothem = vec2(sqrt(3.)/2., .5);
        
        return min(
          max(d1.y, dot(d1, apothem)),
          max(d2.y, dot(d2, apothem))
        );
      }

      float zoomFunc(float len){
        return asin(clamp(len, 0.0, 0.999));
      }

      void main() {
        vec2 pos = (gl_FragCoord.xy * 2.0 - resolution.xy) / resolution.y;

        float len = length(pos);

        if (len >= 1.0) {
          gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
          return;
        }

        vec2 hexCoord = pos/len*zoomFunc(len);
        float angle = time*rotSpeed;
        hexCoord *= mat2(
          cos(angle), sin(angle),
          -sin(angle), cos(angle)
        );
        hexCoord = hexCoord*hexCount + moveSpeed*time;
        
        float hexDistVal = hexDistFunc(hexCoord);

        float hexAmt = 0.;
        hexAmt += max(2.*smoothstep(0.92, 0.93, hexDistVal), pow(hexDistVal, 5.));
        hexAmt *= smoothstep(1., 0.99, len);
        
        float glowAmt = sqrt(max(0.0, 1. - len*len));
        
        vec3 col = hexCol*hexAmt + glowCol*glowAmt;

        gl_FragColor = vec4(col, 1.);
      }
    `

    const compileShader = (type: number, source: string) => {
      const shader = gl.createShader(type)
      if (!shader) return null
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compilation error:', gl.getShaderInfoLog(shader))
        gl.deleteShader(shader)
        return null
      }
      return shader
    }

    const vertexShader = compileShader(gl.VERTEX_SHADER, vertexShaderSource)
    const fragmentShader = compileShader(
      gl.FRAGMENT_SHADER,
      fragmentShaderSource
    )
    if (!vertexShader || !fragmentShader) return

    const program = gl.createProgram()
    if (!program) return
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program linking error:', gl.getProgramInfoLog(program))
      gl.deleteProgram(program)
      return
    }

    const positionBuffer = gl.createBuffer()
    if (!positionBuffer) return
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    )

    const setSize = () => {
      const ratio = window.devicePixelRatio || 1
      const size = 140
      canvas.width = size * ratio
      canvas.height = size * ratio
      canvas.style.width = `${size}px`
      canvas.style.height = `${size}px`
      gl.viewport(0, 0, canvas.width, canvas.height)
    }

    setSize()
    window.addEventListener('resize', setSize)

    gl.useProgram(program)
    const positionLocation = gl.getAttribLocation(program, 'position')
    gl.enableVertexAttribArray(positionLocation)
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

    const resolutionLocation = gl.getUniformLocation(program, 'resolution')
    const timeLocation = gl.getUniformLocation(program, 'time')

    gl.clearColor(0, 0, 0, 0)

    const start = performance.now()

    webglResourcesRef.current = {
      gl,
      program,
      vertexShader,
      fragmentShader,
      positionBuffer,
      positionLocation,
      resolutionLocation,
      timeLocation,
      startTime: start,
      setSize,
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      if (webglResourcesRef.current) {
        const resources = webglResourcesRef.current
        if (resources.setSize) {
          window.removeEventListener('resize', resources.setSize)
        }
        if (resources.positionBuffer) {
          resources.gl?.deleteBuffer(resources.positionBuffer)
        }
        if (resources.program) {
          resources.gl?.deleteProgram(resources.program)
        }
        if (resources.vertexShader) {
          resources.gl?.deleteShader(resources.vertexShader)
        }
        if (resources.fragmentShader) {
          resources.gl?.deleteShader(resources.fragmentShader)
        }
        webglResourcesRef.current = null
      }
      isInitializedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isLoad) {
      setIsVisible(true)
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }
    } else {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      debounceTimeoutRef.current = setTimeout(() => {
        setIsVisible(false)
        debounceTimeoutRef.current = null
      }, 1000)
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }
    }
  }, [isLoad])

  useEffect(() => {
    const resources = webglResourcesRef.current
    if (!resources || !canvasRef.current) return

    const canvas = canvasRef.current
    const {
      gl,
      program,
      positionLocation,
      positionBuffer,
      resolutionLocation,
      timeLocation,
      startTime,
    } = resources

    if (!gl || !program) return

    gl.useProgram(program)
    gl.enableVertexAttribArray(positionLocation)
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)
    gl.clearColor(0, 0, 0, 0)

    const render = () => {
      if (!isVisible) {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
          animationRef.current = null
        }
        return
      }

      const now = performance.now()
      const t = (now - startTime) / 1000
      gl.clear(gl.COLOR_BUFFER_BIT)
      if (resolutionLocation) {
        gl.uniform2f(resolutionLocation, canvas.width, canvas.height)
      }
      if (timeLocation) {
        gl.uniform1f(timeLocation, t)
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      animationRef.current = requestAnimationFrame(render)
    }

    if (isVisible) {
      if (!animationRef.current) {
        animationRef.current = requestAnimationFrame(render)
      }
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }
  }, [isVisible])

  const isMainPage = pathname === '/main'
  const shouldHideLoader = hideLoaderPages.includes(pathname)
  const finalIsVisible = isVisible && !shouldHideLoader

  return (
    <Overlay $isVisible={finalIsVisible} $isMainPage={isMainPage}>
      <Canvas ref={canvasRef} id="loader" style={{opacity: 0.6}} />
      <LoadingText>&nbsp;Loading...</LoadingText>
    </Overlay>
  )
}

const Overlay = styled('div')<{$isVisible: boolean; $isMainPage: boolean}>(
  ({$isVisible, $isMainPage}) => ({
    position: 'absolute',
    right: $isMainPage ? '120px' : '60px',
    bottom: '40px',
    height: '140px',
    width: '140px',
    zIndex: '1500',
    display: $isVisible ? 'block' : 'none',
  })
)

const Canvas = styled('canvas')(() => ({
  width: '140px',
  height: '140px',
  display: 'block',
}))

const LoadingText = styled('div')(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  color: '#22ffff',
  fontSize: '28px',
  fontFamily: 'Me, sans-serif',
  pointerEvents: 'none',
  zIndex: 2,
}))

export default ModalLoader
